import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@noor/ui';
import {
  resolvePreset,
  matchPreset,
  startOfDay,
  isSameDay,
  formatDisplayDate,
  type LegacyDateRange,
  type LegacyRangePresetId,
} from '../lib/date-range';
import { LegacyCalendar } from './legacy-calendar';
import { LegacyRangePresets } from './legacy-range-presets';

/**
 * The legacy date-range picker — the old panel's `DateRangeInput` plus its
 * drawer, as one popover: calendar on the left, presets on the right, and a
 * "Selected Dates" bar with Apply beneath.
 *
 * The draft range is LOCAL to the popover and only lifted on Apply. The
 * original worked the same way, and it matters: the range feeds a list query,
 * so committing each half-made selection would fire a request for a range the
 * merchant is still in the middle of choosing.
 */
interface LegacyDateRangePickerProps {
  /** The committed range, as the trigger label renders it. */
  value: LegacyDateRange;
  /** Days after this are unselectable. Defaults to today — no future ranges. */
  maxDate?: Date;
  onChange: (range: LegacyDateRange) => void;
}

export function LegacyDateRangePicker({
  value,
  maxDate = new Date(),
  onChange,
}: LegacyDateRangePickerProps) {
  const { t } = useTranslation('legacy');
  const [open, setOpen] = useState(false);

  // Draft state, seeded from the committed value each time the popover opens —
  // keyed remount rather than an effect, so cancelling discards cleanly.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-12 min-w-0 shrink cursor-pointer items-center gap-2 rounded-[96px] bg-[var(--lg-surface)] px-4 text-[16px] text-[var(--lg-grey-500)] transition-opacity hover:opacity-80"
        >
          <CalendarIcon className="size-5 shrink-0 text-[var(--lg-orange-500)]" />
          {/* The «Выбранные даты:» prefix is what makes this pill ~330px wide,
              and it is pure label — the dates beside it say the same thing. It
              drops below `lg` so the toolbar row fits a tablet/phone without
              overflowing, while the dates themselves always stay. */}
          <span className="hidden whitespace-nowrap lg:inline">
            {isSameDay(value.from, value.to)
              ? `${t('date.one_selected_date')}: `
              : `${t('date.selected_date')}: `}
          </span>
          <span className="truncate font-semibold whitespace-nowrap text-[var(--lg-fg)]">
            {isSameDay(value.from, value.to)
              ? formatDisplayDate(value.from)
              : `${formatDisplayDate(value.from)} - ${formatDisplayDate(value.to)}`}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        data-legacy
        align="end"
        sideOffset={8}
        // `data-legacy` scopes the `--lg-*` tokens to this portalled subtree —
        // it renders outside `LegacyRoot`, so without it the panels would
        // resolve every legacy variable to nothing.
        //
        // The wrapper is OPAQUE (page-background, not transparent): the picker
        // is three separate rounded panels with gaps between them, and a
        // transparent backdrop lets the table underneath show through those
        // gaps. `.legacy-root` supplies that background along with the font.
        className="legacy-root w-auto rounded-[24px] border-0 p-2 shadow-popover-halo"
      >
        {open && (
          <PickerBody
            key={`${value.from.toISOString()}-${value.to.toISOString()}`}
            initial={value}
            maxDate={maxDate}
            onApply={(next) => {
              onChange(next);
              setOpen(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

function PickerBody({
  initial,
  maxDate,
  onApply,
}: {
  initial: LegacyDateRange;
  maxDate: Date;
  onApply: (range: LegacyDateRange) => void;
}) {
  const { t } = useTranslation('legacy');

  const [draft, setDraft] = useState<LegacyDateRange>(initial);
  const [month, setMonth] = useState<Date>(initial.from);
  /** True once the start is set and we are waiting for the end. */
  const [awaitingEnd, setAwaitingEnd] = useState(false);

  const activePreset = matchPreset(draft);

  const selectDay = (day: Date) => {
    const picked = startOfDay(day);

    if (!awaitingEnd) {
      // First click collapses the range onto the picked day, so a half-made
      // selection reads as one highlighted date rather than a stale span.
      setDraft({ from: picked, to: picked });
      setAwaitingEnd(true);
      return;
    }

    // Second click closes the range, swapping if picked backwards.
    setDraft(
      picked < draft.from
        ? { from: picked, to: draft.from }
        : { from: draft.from, to: picked },
    );
    setAwaitingEnd(false);
  };

  const selectPreset = (preset: LegacyRangePresetId) => {
    const range = resolvePreset(preset);
    setDraft(range);
    setMonth(range.from);
    setAwaitingEnd(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row">
        <LegacyCalendar
          month={month}
          range={draft}
          maxDate={maxDate}
          onMonthChange={setMonth}
          onSelectDay={selectDay}
        />
        <LegacyRangePresets active={activePreset} onSelect={selectPreset} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-[24px] bg-[var(--lg-surface)] p-6">
        <div className="flex flex-col gap-1">
          <span className="text-[16px] font-semibold text-[var(--lg-fg)]">
            {t('date.selected_date')}
          </span>
          <span className="text-[16px] text-[var(--lg-grey-600)]">
            {formatDisplayDate(draft.from)} — {formatDisplayDate(draft.to)}
          </span>
        </div>

        <button
          type="button"
          onClick={() => onApply(draft)}
          className="h-12 cursor-pointer rounded-[96px] bg-[var(--lg-orange-500)] px-8 text-[16px] font-semibold text-[var(--lg-surface)] transition-opacity hover:opacity-90"
        >
          {t('date.accept')}
        </button>
      </div>
    </div>
  );
}
